/**
 * Healing & repair logic — medic heal order + passive building repair.
 * Pure functions operating on TurnWorld, no Phaser dependencies.
 */
import type { TurnWorld, Unit, UnitType, PlayerId } from './types';
import { UNIT_DEFS, resolveBaseType } from './units';
import { BUILDING_DEFS } from './buildings';

/** Unit types the medic can heal (land units). */
export const MEDIC_HEAL_TYPES: ReadonlySet<UnitType> = new Set<UnitType>([
  'infantry', 'infantry-bazooka', 'recon', 'cavalry', 'jeep',
  'light-tank', 'medium-tank', 'heavy-tank', 'apc',
  'tank-destroyer', 'long-range-artillery', 'anti-tank-artillery',
  'aa-flakpanzer', 'supply-truck', 'medic',
]);

/** Chebyshev (king-move) distance between two grid positions. */
function chebyshev(a: { col: number; row: number }, b: { col: number; row: number }): number {
  return Math.max(Math.abs(a.col - b.col), Math.abs(a.row - b.row));
}

/** Heal amount: up to 50% of maxHp, capped at missing HP. */
function healAmount(unit: Unit): number {
  const def = UNIT_DEFS[unit.type];
  return Math.min(Math.floor(def.maxHp * 0.5), def.maxHp - unit.hp);
}

export interface HealResult {
  unitId: number;
  hpBefore: number;
  hpAfter: number;
}

/**
 * Returns the list of units the given medic can heal this turn.
 * Adjacent (Chebyshev ≤ 1), friendly, healable type, wounded, not self.
 */
export function computeHealTargets(world: TurnWorld, medicId: number): Unit[] {
  const medic = world.units.get(medicId);
  if (!medic) return [];

  const targets: Unit[] = [];
  for (const [, u] of world.units) {
    if (u.id === medicId) continue; // can't heal self
    if (u.owner !== medic.owner) continue; // friendly only
    if (!MEDIC_HEAL_TYPES.has(u.type) && !MEDIC_HEAL_TYPES.has(resolveBaseType(u.type))) continue; // healable type
    if (u.hp >= UNIT_DEFS[u.type].maxHp) continue; // already full HP
    if (chebyshev(medic, u) > 1) continue; // must be adjacent
    targets.push(u);
  }
  return targets;
}

/**
 * Execute medic heal: mutates HP on all adjacent healable units, sets medic acted.
 * Returns list of heal results for animation.
 */
export function executeHeal(world: TurnWorld, medicId: number): HealResult[] {
  const medic = world.units.get(medicId);
  if (!medic) return [];

  const targets = computeHealTargets(world, medicId);
  const results: HealResult[] = [];

  for (const target of targets) {
    const amount = healAmount(target);
    if (amount <= 0) continue;
    const hpBefore = target.hp;
    target.hp += amount;
    results.push({ unitId: target.id, hpBefore, hpAfter: target.hp });
  }

  medic.acted = true;
  medic.movePoints = 0;
  return results;
}

export interface RepairResult {
  unitId: number;
  hpBefore: number;
  hpAfter: number;
}

export interface BuildingRepairResult {
  buildingId: number;
  col: number;
  row: number;
  repairs: RepairResult[];
}

/**
 * Returns the list of units a building can repair this turn.
 * Adjacent (Chebyshev ≤ 1), friendly, matching type (or all for repairsAll), wounded.
 */
export function getRepairableUnits(world: TurnWorld, buildingId: number): Unit[] {
  const building = world.buildings.get(buildingId);
  if (!building || building.owner == null) return [];

  const bDef = BUILDING_DEFS[building.type];
  // Buildings that can't repair anything
  if (!bDef.repairsAll && !bDef.recruits) return [];

  const targets: Unit[] = [];
  for (const [, u] of world.units) {
    if (u.owner !== building.owner) continue; // friendly only
    if (u.hp >= UNIT_DEFS[u.type].maxHp) continue; // already full HP
    if (chebyshev(building, u) > 1) continue; // must be adjacent (Chebyshev ≤ 1)
    // Type check: repairsAll bypasses, otherwise must be in recruits list (or base type)
    if (!bDef.repairsAll && !bDef.recruits!.includes(u.type) && !bDef.recruits!.includes(resolveBaseType(u.type))) continue;
    targets.push(u);
  }
  return targets;
}

/**
 * Execute passive repairs for all buildings owned by the given player.
 * Returns list of building repair results for animation.
 */
export function executeRepairs(world: TurnWorld, playerId: PlayerId): BuildingRepairResult[] {
  const results: BuildingRepairResult[] = [];

  for (const [, building] of world.buildings) {
    if (building.owner !== playerId) continue;

    const units = getRepairableUnits(world, building.id);
    if (units.length === 0) continue;

    const repairs: RepairResult[] = [];
    for (const unit of units) {
      const amount = healAmount(unit);
      if (amount <= 0) continue;
      const hpBefore = unit.hp;
      unit.hp += amount;
      repairs.push({ unitId: unit.id, hpBefore, hpAfter: unit.hp });
    }

    if (repairs.length > 0) {
      results.push({ buildingId: building.id, col: building.col, row: building.row, repairs });
    }
  }

  return results;
}
